import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ImportExport as ImportExportIcon, Download as DownloadIcon, Upload as UploadIcon } from '@mui/icons-material';

interface Section {
  key: string;
  label: string;
  exportPath: string;
  importPath: string;
  fileName: string;
}

// Manufacturer/Formulation/Unit/ProductCategory/GeneStack/Season's own
// GET-all endpoint already returns everything needed to round-trip (Id +
// every field) — no separate "/export" endpoint was needed for those.
// Products alone gets a dedicated /export endpoint since its export has to
// embed Manufacturer/Formulation/GeneStack/ActiveIngredients/Categories by
// {id, name} rather than a bare Guid (see ProductExportDto).
const SECTIONS: Section[] = [
  { key: 'products', label: 'Products', exportPath: '/api/products/export', importPath: '/api/products/import', fileName: 'products.json' },
  { key: 'product-categories', label: 'Categories', exportPath: '/api/product-categories?includeArchived=true', importPath: '/api/product-categories/import', fileName: 'categories.json' },
  { key: 'units', label: 'Units', exportPath: '/api/units?includeArchived=true', importPath: '/api/units/import', fileName: 'units.json' },
  { key: 'formulations', label: 'Formulations', exportPath: '/api/formulations?includeArchived=true', importPath: '/api/formulations/import', fileName: 'formulations.json' },
  { key: 'gene-stacks', label: 'Gene Stacks', exportPath: '/api/gene-stacks?includeArchived=true', importPath: '/api/gene-stacks/import', fileName: 'gene-stacks.json' },
  { key: 'manufacturers', label: 'Manufacturers', exportPath: '/api/manufacturers?includeArchived=true', importPath: '/api/manufacturers/import', fileName: 'manufacturers.json' },
  { key: 'seasons', label: 'Time Periods', exportPath: '/api/time-periods?includeArchived=true', importPath: '/api/time-periods/import', fileName: 'time-periods.json' },
];

const ReferenceDataExportImport: React.FC = () => {
  const auth = useAuth();
  const authHeaders = () => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` });

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, { importedCount: number; skippedCount: number; skippedNames: string[] } | undefined>>({});
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleExport = async (section: Section) => {
    setBusyKey(section.key);
    setError(null);
    try {
      const res = await fetch(section.exportPath, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to export ${section.label}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = section.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to export ${section.label}`);
    } finally {
      setBusyKey(null);
    }
  };

  const handleImportFile = async (section: Section, file: File) => {
    setBusyKey(section.key);
    setError(null);
    try {
      const text = await file.text();
      const items = JSON.parse(text);
      const res = await fetch(section.importPath, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(items),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to import ${section.label}`);
      }
      const result = await res.json();
      setResults((prev) => ({ ...prev, [section.key]: result }));
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to import ${section.label} — check the file is valid JSON from a matching export.`);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Box>
      <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={1}>
        <ImportExportIcon />
        Export / Import
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Download any of these reference-data tables as JSON, or upload a previously-exported file back in.
        Importing never duplicates or overwrites — a row already present (matched by its id or by name) is skipped.
        Products carry their Manufacturer/Formulation/Gene Stack/Active Ingredients/Categories with them, creating
        any of those that don't already exist on this instance.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
              <TableCell>Last Import Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SECTIONS.map((section) => {
              const result = results[section.key];
              const busy = busyKey === section.key;
              return (
                <TableRow key={section.key} hover>
                  <TableCell>{section.label}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
                      disabled={busy}
                      onClick={() => handleExport(section)}
                      sx={{ mr: 1 }}
                    >
                      Export
                    </Button>
                    <Button
                      size="small"
                      startIcon={<UploadIcon />}
                      disabled={busy}
                      onClick={() => fileInputs.current[section.key]?.click()}
                    >
                      Import
                    </Button>
                    <input
                      type="file"
                      accept="application/json"
                      hidden
                      ref={(el) => { fileInputs.current[section.key] = el; }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImportFile(section, file);
                        e.target.value = '';
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {result && (
                      <Typography variant="caption" color="text.secondary">
                        Imported {result.importedCount}, skipped {result.skippedCount} duplicate{result.skippedCount === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReferenceDataExportImport;
